import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useContent, t, parseHiddenSet } from "@/hooks/useContent";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About" },
  { to: "/testimonials", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

const FOOTER_ICON = {
  mappin: MapPin,
  phone: Phone,
  mail: Mail,
  external: ExternalLink,
};

function parseFooterColumns(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map((c) => ({
      title: c?.title || "",
      hidden: !!c?.hidden,
      items: Array.isArray(c?.items)
        ? c.items.map((i) => ({ label: i?.label || "", href: i?.href || "", icon: i?.icon || "", hidden: !!i?.hidden }))
        : [],
    }));
  } catch { return []; }
}

function FooterItem({ item, testid }) {
  const Icon = item.icon && FOOTER_ICON[item.icon.toLowerCase()];
  const IconNode = Icon ? <Icon size={16} className="mt-0.5 shrink-0" /> : null;
  const content = (
    <span className="flex items-start gap-2 text-sm text-white/80 hover:text-[#F2A900]">
      {IconNode}
      <span>{item.label}</span>
    </span>
  );
  const href = item.href;
  if (!href) return <div data-testid={testid} className="text-sm text-white/70 flex items-start gap-2">{IconNode}<span>{item.label}</span></div>;
  const isInternal = href.startsWith("/") && !href.startsWith("//");
  if (isInternal) return <Link to={href} data-testid={testid}>{content}</Link>;
  const isExternal = href.startsWith("http");
  return (
    <a href={href} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noreferrer" : undefined} data-testid={testid}>
      {content}
    </a>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const c = useContent();
  const hidden = parseHiddenSet(c);
  const H = (id) => hidden.has(id);
  useEffect(() => setOpen(false), [loc.pathname]);

  const columns = parseFooterColumns(t(c, "footer.columns", "")).filter((col) => !col.hidden);
  const copyright = (t(c, "footer.copyright", "© {year} B-Raw Snack Food Product. All rights reserved.") || "").replace("{year}", String(new Date().getFullYear()));

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-black/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="B-Raw" className="h-10 sm:h-12 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                    isActive
                      ? "bg-[#111] text-[#F2A900]"
                      : "text-[#111] hover:bg-black/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-black/10 bg-white"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="md:hidden border-t border-black/10 bg-white"
          >
            <div className="px-4 py-3 flex flex-col">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mnav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-base font-semibold ${
                      isActive ? "bg-[#111] text-[#F2A900]" : "text-[#111]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {!H("footer.section") && (
      <footer className="bg-[#111] text-[#FDFBF7] mt-20" data-testid="site-footer">
        {!(H("footer.brand.section") && H("footer.columns.section")) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-10 md:grid-cols-4">
          {!H("footer.brand.section") && (
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="B-Raw" className="h-16 w-auto" />
              {!H("footer.tagline") && (
                <p data-testid="footer-tagline" className="mt-4 text-sm text-white/70 max-w-md leading-relaxed whitespace-pre-line">
                  {t(c, "footer.tagline", "Founded by Devender Yadav — a BTech in Food Engineering with 9+ years in the food industry. Made in Sirsa, loved across Bharat.")}
                </p>
              )}
            </div>
          )}
          {!H("footer.columns.section") && columns.map((col, i) => (
            <div key={i} data-testid={`footer-col-${i}`}>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900]">
                {col.title}
              </div>
              <div className="mt-3 space-y-2">
                {col.items.filter((it) => !it.hidden).map((it, j) => (
                  <FooterItem key={j} item={it} testid={`footer-item-${i}-${j}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
        )}
        {!H("footer.copyright.section") && (
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4 text-xs text-white/50 flex flex-col md:flex-row items-center justify-between gap-2">
            {!H("footer.copyright") && <div data-testid="footer-copyright">{copyright}</div>}
            {!H("footer.rightline") && <div data-testid="footer-rightline">{t(c, "footer.rightline", "Tasty & Crunchy!")}</div>}
          </div>
        </div>
        )}
      </footer>
      )}
    </div>
  );
}
